import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { WritePageModal } from '@/components/WritePageModal';
import { WriteRunPanel, RUN_KEY } from '@/components/WriteRunPanel';
import {
  SITE_MAP_KEY, getWriteBoard, queueWriteBoard, setWriteHold, extractScaffoldRules, commitScaffoldRules,
  type WriteBoard as Board, type WriteBoardAction, type WriteBoardRow, type RulesProposal, type ScaffoldNote,
} from '@/data/client';

/* The Write site readiness board (docs/bulk-write-plan.md, Phase A): every
   page still to be written, in site order, with what stands between it and a
   good first draft. Readiness is computed from the page's plan, never ticked;
   "forecast thin" is a warning, never a block. The bulk actions are model
   calls, so they queue and the five-minute cron works through them; the
   board polls while anything is queued. */

const BOARD_KEY = ['siteMap', 'writeBoard'];
const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const ACTION_LABEL: Record<WriteBoardAction, string> = { brief: 'suggesting a plan', questions: 'writing questions', check: 'checking the Vault', outline: 'suggesting an outline' };

export function WriteBoard({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: BOARD_KEY,
    queryFn: getWriteBoard,
    refetchInterval: (q) => ((q.state.data as Board | undefined)?.rows.some((r) => r.queued) ? 30_000 : false),
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [rules, setRules] = useState<{ row: WriteBoardRow; proposal: RulesProposal } | null>(null);
  const [extracting, setExtracting] = useState<string | null>(null);
  const [writing, setWriting] = useState<WriteBoardRow | null>(null);

  const rows = data?.rows ?? [];
  const counts = useMemo(() => ({
    ready: rows.filter((r) => r.ready).length,
    scaffolded: rows.filter((r) => r.status === 'scaffolded').length,
    held: rows.filter((r) => r.hold).length,
    thin: rows.filter((r) => r.thin.length).length,
    queued: rows.filter((r) => r.queued).length,
  }), [rows]);
  const selected = rows.filter((r) => picked.has(r.id));
  const put = (b: Board) => { qc.setQueryData(BOARD_KEY, b); qc.invalidateQueries({ queryKey: SITE_MAP_KEY, exact: true }); qc.invalidateQueries({ queryKey: RUN_KEY }); };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(''); setNote('');
    try { await fn(); } catch (e) { setErr(errMsg(e, 'That did not work.')); } finally { setBusy(false); }
  };
  const queue = (action: WriteBoardAction | null) => run(async () => {
    const r = await queueWriteBoard(selected.map((x) => x.id), action);
    put(r.board);
    setPicked(new Set());
    setNote(action
      ? `${plural(r.queued, 'page')} queued for ${ACTION_LABEL[action]}. Three pages run every five minutes; this board refreshes while they do.`
      : `${plural(r.queued, 'page')} taken out of the queue.`);
  });
  const toggle = (id: string) => setPicked((cur) => { const n = new Set(cur); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allPicked = rows.length > 0 && picked.size === rows.length;

  return (
    <div className="wb">
      <div className="wb__head">
        <div>
          <h3><Icon name="edit" size={15} /> Write site</h3>
          <p className="dash-muted">
            {isLoading || !data ? 'Loading…' : `${plural(rows.length, 'page')} to write · ${counts.scaffolded} scaffolded · ${counts.ready} ready · ${counts.held} held · ${counts.thin} forecast thin · ${data.written} already written`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} iconLeft={<Icon name="chevron-left" size={13} />}>Back to the site map</Button>
      </div>

      {data && !data.faqPush && (
        <p className="wb__warn"><Icon name="alert-circle" size={13} /> FAQ push is off for this client, so these pages will ship without FAQs. FAQs come only from the FAQ editor; turn push on in Admin → Content → Expert Studio.</p>
      )}
      <WriteRunPanel onChanged={() => void qc.invalidateQueries({ queryKey: BOARD_KEY })} />
      {(note || err) && <p className={`gen-note smb__note${err ? ' is-err' : ''}`}>{err || note}</p>}
      {error && <p className="gen-note smb__note is-err">{errMsg(error, 'Could not load the board.')}</p>}

      {rows.length > 0 && (
        <div className="wb__tools">
          <label className="smb__toggle"><input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.id)))} /> All</label>
          <Button variant="outline" size="sm" disabled={busy || !selected.length} onClick={() => queue('brief')} iconLeft={<Icon name="sparkles" size={13} />}>Suggest plans ({selected.length})</Button>
          <Button variant="outline" size="sm" disabled={busy || !selected.length} onClick={() => queue('check')} iconLeft={<Icon name="search" size={13} />}>Check the Vault ({selected.length})</Button>
          <Button variant="outline" size="sm" disabled={busy || !selected.length} onClick={() => queue('outline')} iconLeft={<Icon name="list" size={13} />}>Suggest outlines ({selected.length})</Button>
          <Button variant="ghost" size="sm" disabled={busy || !selected.length} onClick={() => queue('questions')}>Write questions without the Vault ({selected.length})</Button>
          {counts.queued > 0 && <Button variant="ghost" size="sm" disabled={busy || !selected.some((r) => r.queued)} onClick={() => queue(null)}>Take out of the queue</Button>}
          <span className="dash-muted wb__hint">Suggest plans fills only empty type, keywords and brief. Check the Vault answers what it can and writes follow-ups. Suggest outlines skips outlines someone has edited and ones still current with the Vault.</span>
        </div>
      )}

      {data && !rows.length && <p className="smb__empty">Every page on the site map is written. New pages appear here once they are planned.</p>}

      <ul className="wb__list">
        {rows.map((r) => (
          <BoardLine key={r.id} row={r} picked={picked.has(r.id)} busy={busy} extracting={extracting === r.id} onPick={() => toggle(r.id)}
            onHold={(hold) => run(async () => { put((await setWriteHold(r.id, hold)).board); })}
            onWrite={() => setWriting(r)}
            onExtract={() => { setExtracting(r.id); void run(async () => { try { setRules({ row: r, proposal: await extractScaffoldRules(r.id) }); } finally { setExtracting(null); } }); }} />
        ))}
      </ul>

      {writing && <WritePageModal pageId={writing.id} name={writing.name} staging={!!data?.stagingUrl} onClose={() => { setWriting(null); void qc.invalidateQueries({ queryKey: BOARD_KEY }); }} />}

      {rules && (
        <RulesModal
          row={rules.row}
          proposal={rules.proposal}
          onClose={() => setRules(null)}
          onCommitted={(b, msg) => { put(b); setRules(null); setNote(msg); }}
        />
      )}
    </div>
  );
}

const noteText = (n: ScaffoldNote) => (['purpose', 'count', 'title', 'body', 'keyword', 'optional', 'topic', 'evidence'] as const).filter((k) => n[k]).map((k) => `${k}: ${n[k]}`).join('\n');

/* Review before commit: one editable note per block, the site rules file, and
   whether to spread the notes to the other scaffolds without any. Notes are
   written into the file as YAML comments by the Worker; nothing else in the
   scaffold changes. */
function RulesModal({ row, proposal, onClose, onCommitted }: {
  row: WriteBoardRow; proposal: RulesProposal; onClose: () => void; onCommitted: (b: Board, msg: string) => void;
}) {
  const [texts, setTexts] = useState<Record<number, string>>(() => Object.fromEntries(proposal.blocks.map((b) => [b.index, noteText(b.note)])));
  const [rulesText, setRulesText] = useState(proposal.rules.proposed);
  const [withRules, setWithRules] = useState(true);
  const [spread, setSpread] = useState(proposal.spreadable > 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const commit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await commitScaffoldRules(row.id, {
        notes: proposal.blocks.filter((b) => !b.noted).map((b) => ({ index: b.index, text: texts[b.index] || '' })),
        rules: withRules ? rulesText : null,
        spread,
      });
      onCommitted(r.board, `${plural(r.committed.length, 'file')} committed${r.skipped.length ? `, ${r.skipped.length} unchanged` : ''}${r.remaining ? `; ${r.remaining} more scaffolds to note — run Extract rules again on any of them, or press commit again here` : ''}.`);
    } catch (e) { setErr(errMsg(e, 'Could not commit.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={onClose} />
      <div className="admin-modal__panel wb__rules">
        <div className="admin-modal__head">
          <div>
            <h3>Section notes for {row.name}</h3>
            <p className="dash-muted">
              Proposed from <code>{proposal.path}</code>{proposal.components.length ? ` and ${plural(proposal.components.length, 'component file')}` : ' (no component files matched its blocks; the notes are a guess from the page alone)'}. Edit anything; empty lines are left out. One <code>key: value</code> per line.
            </p>
          </div>
        </div>
        {err && <p className="gen-note smb__note is-err">{err}</p>}
        <ol className="wb__blocks">
          {proposal.blocks.map((b) => (
            <li key={b.index}>
              <strong>{b.name}</strong>
              {b.noted
                ? <span className="dash-muted"> · already has a note; left as it is</span>
                : <Textarea rows={Math.max(2, (texts[b.index] || '').split('\n').length)} value={texts[b.index] || ''} onChange={(e) => setTexts((t) => ({ ...t, [b.index]: e.target.value }))} />}
            </li>
          ))}
        </ol>
        <label className="smb__toggle"><input type="checkbox" checked={withRules} onChange={(e) => setWithRules(e.target.checked)} /> Commit the site rules file, <code>{proposal.rules.path}</code>{proposal.rules.existing ? ' (replaces the current one)' : ''}</label>
        {withRules && <Textarea rows={10} value={rulesText} onChange={(e) => setRulesText(e.target.value)} />}
        {proposal.spreadable > 0 && (
          <label className="smb__toggle"><input type="checkbox" checked={spread} onChange={(e) => setSpread(e.target.checked)} /> Also add these notes to {plural(proposal.spreadable, 'other scaffold')} without notes, matched by block</label>
        )}
        <div className="wb__modalactions">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={busy} onClick={commit}>{busy ? 'Committing…' : 'Commit to the repo'}</Button>
        </div>
      </div>
    </div>
  );
}

function BoardLine({ row: r, picked, busy, extracting, onPick, onHold, onExtract, onWrite }: {
  row: WriteBoardRow; picked: boolean; busy: boolean; extracting: boolean; onPick: () => void; onHold: (hold: boolean) => void; onExtract: () => void; onWrite: () => void;
}) {
  const unanswered = r.questions.open + r.questions.sent;
  return (
    <li className={`wb__row${r.hold ? ' is-held' : ''}`} style={{ ['--depth' as string]: r.depth }}>
      <input type="checkbox" checked={picked} onChange={onPick} aria-label={`Select ${r.name}`} />
      <div className="wb__page">
        <div className="smb__name">
          <strong>{r.name}</strong>
          <code>{r.route === '/' ? '/' : r.route.replace(/\/$/, '')}</code>
          {r.pageType && r.pageType !== 'other' && <span className="smb__type">{r.pageType}</span>}
        </div>
        <div className="wb__signals">
          {r.status === 'scaffolded'
            ? r.ready ? <Badge variant="success">Ready</Badge> : r.hold ? <Badge variant="outline">Held</Badge> : <Badge variant="warning">Plan incomplete</Badge>
            : <Badge variant="neutral">Not scaffolded yet</Badge>}
          {r.missing.length > 0 && <span className="smb__flag smb__flag--warn">needs {r.missing.join(', ')}</span>}
          {r.briefSuggested && <span className="smb__flag smb__flag--warn" title="Filled by a suggestion; saving the page in the editor marks it reviewed">plan suggested — review</span>}
          <span className="smb__flag" title={r.vault.checkedAt ? `Checked ${r.vault.checkedAt.slice(0, 16).replace('T', ' ')}` : undefined}>
            <Icon name="search" size={11} /> {r.vault.checkedAt ? (r.vault.stale ? 'Vault changed since the check' : 'checked against the Vault') : 'not checked against the Vault'}
          </span>
          {(unanswered > 0 || r.questions.answered > 0) && (
            <span className="smb__flag">{r.questions.open} open{r.questions.followups ? ` (${r.questions.followups} follow-ups)` : ''} · {r.questions.sent} sent · {r.questions.answered} answered</span>
          )}
          {r.readiness && <span className="smb__flag" title={r.readiness.service}>interviews {r.readiness.covered} of {r.readiness.total}</span>}
          {r.status === 'scaffolded' && (
            <span className={`smb__flag${r.sectionNotes === 0 ? ' smb__flag--warn' : ''}`}>
              {r.sectionNotes == null ? 'scaffold not read yet — sync from repo' : r.sectionNotes === 0 ? 'no section notes in the scaffold' : `${plural(r.sectionNotes, 'section note')}`}
            </span>
          )}
          <span className={`smb__flag${!r.outline || r.outline.stale ? ' smb__flag--warn' : ''}`} title={r.outline?.at ? `Outlined ${r.outline.at.slice(0, 16).replace('T', ' ')}${r.outline.edited ? ', edited by Triton' : ''}` : 'The outline shapes the scaffold; suggest one before scaffolding'}>
            {!r.outline ? 'no outline' : r.outline.stale ? `outline out of date (${plural(r.outline.topics, 'topic')})` : `outline: ${plural(r.outline.topics, 'topic')}`}
          </span>
          <span className="smb__flag">{r.faqs.live} FAQs on the page{r.faqs.waiting ? `, ${r.faqs.waiting} awaiting review` : ''}</span>
          {r.queued && <Badge variant="accent">Queued: {ACTION_LABEL[r.queued]}</Badge>}
          {r.ready && <button type="button" className="smb__link" disabled={busy} onClick={onWrite}>Write this page</button>}
          {r.status === 'scaffolded' && <button type="button" className="smb__link" disabled={busy} onClick={onExtract} title="Propose section notes for this scaffold from the site's components; you review before anything is committed">{extracting ? 'Reading the components…' : r.sectionNotes ? 'Re-extract rules' : 'Extract rules'}</button>}
        </div>
        {r.thin.length > 0 && <p className="wb__thin"><Icon name="alert-circle" size={12} /> Forecast thin: {r.thin.join('; ')}.</p>}
      </div>
      <label className="smb__toggle wb__hold" title="A held page is left out of Write site">
        <input type="checkbox" checked={r.hold} disabled={busy} onChange={(e) => onHold(e.target.checked)} /> Hold
      </label>
    </li>
  );
}
